/**
 * "How Cello compares" section — Cello vs Traditional Stylist / Stitch Fix /
 * Fashivly across 7 dimensions. Cello is a continuous dark-olive band; the
 * three competitor columns sit on the cream page background. Static content;
 * table copy is final per the design brief.
 */
import { Fragment } from "react";

interface Cell {
  /** true = orange checkmark; false = muted dash; string = value text (e.g. price). */
  mark: boolean | string;
  caption?: string;
}

interface Row {
  label: string;
  cello: Cell;
  traditional: Cell;
  stitchFix: Cell;
  fashivly: Cell;
}

const ROWS: Row[] = [
  {
    label: "INVESTMENT",
    cello: { mark: "$365 – $550", caption: "flat fee, everything included" },
    traditional: { mark: "$2,000 – $10,000", caption: "per project" },
    stitchFix: { mark: "Free*", caption: "$25 fee waived with purchase" },
    fashivly: { mark: "$149 for 10 looks", caption: "$596 for 40 looks, Cello's average minimum" },
  },
  {
    label: "STYLE YOUR EXISTING CLOTHES",
    cello: { mark: true },
    traditional: { mark: true },
    stitchFix: { mark: false, caption: "shopping only" },
    fashivly: { mark: false, caption: "shopping only" },
  },
  {
    label: "WHERE YOU SHOP",
    cello: { mark: true, caption: "anywhere" },
    traditional: { mark: true, caption: "anywhere + affiliate retailers" },
    stitchFix: { mark: false, caption: "Stitch Fix inventory only" },
    fashivly: { mark: false, caption: "affiliate retailers" },
  },
  {
    label: "PERSONALIZATION",
    cello: { mark: true, caption: "closet, budget, lifestyle + body shape" },
    traditional: { mark: true, caption: "closet, budget, lifestyle + body shape" },
    stitchFix: { mark: false, caption: "about 6 minutes per fix" },
    fashivly: { mark: false, caption: "based on intake quiz" },
  },
  {
    label: "ONBOARDING",
    cello: { mark: true, caption: "virtual, 20 – 30 min closet capture" },
    traditional: { mark: false, caption: "1 – 4 hours, virtual or in person" },
    stitchFix: { mark: true, caption: "short quiz, virtual" },
    fashivly: { mark: true, caption: "short quiz, virtual" },
  },
  {
    label: "YOUR STYLIST",
    cello: { mark: true, caption: "institution-trained, matched to you" },
    traditional: { mark: true, caption: "you hire them, training varies" },
    stitchFix: { mark: false, caption: "assigned per box" },
    fashivly: { mark: false, caption: "assigned, training varies" },
  },
  {
    label: "IN-PERSON SHOPPING TRIPS & FITTINGS",
    cello: { mark: false, caption: "sizing and fit checks via message" },
    traditional: { mark: true, caption: "billed hourly" },
    stitchFix: { mark: false },
    fashivly: { mark: false },
  },
];

const OrangeCheck = ({ onOlive = false }: { onOlive?: boolean }) => (
  <span
    className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full"
    style={{
      border: onOlive
        ? "1.5px solid rgba(250,241,232,0.32)"
        : "1.5px solid #E3DCCB",
    }}
  >
    <span
      aria-hidden="true"
      className="block h-[5px] w-[9px] -translate-y-px rotate-[-45deg]"
      style={{
        borderLeft: onOlive ? "2px solid #F2692C" : "2px solid #E05A17",
        borderBottom: onOlive ? "2px solid #F2692C" : "2px solid #E05A17",
      }}
    />
  </span>
);

const Dash = ({ onOlive = false }: { onOlive?: boolean }) => (
  <span
    className="text-[15px] font-semibold"
    style={{ color: onOlive ? "rgba(250,241,232,0.45)" : "#C9BFA9" }}
  >
    —
  </span>
);

const renderMark = (mark: Cell["mark"], onOlive: boolean) => {
  if (mark === true) return <OrangeCheck onOlive={onOlive} />;
  if (mark === false) return <Dash onOlive={onOlive} />;
  return (
    <span
      className="font-bold"
      style={{
        color: onOlive ? "#FCF6EC" : "#2E2B15",
        fontSize: onOlive ? "15px" : "13.5px",
      }}
    >
      {mark}
    </span>
  );
};

interface CompetitorCompareBassProps {
  /**
   * When true, drops the cream section wrapper + big title/subline and just
   * renders the table + footnote. Used when embedding inside another
   * container (e.g. as an answer inside FAQSectionBass).
   */
  inline?: boolean;
}

const CompetitorCompareBass = ({ inline = false }: CompetitorCompareBassProps = {}) => {
  const table = (
    <>
      {/* Safety-net horizontal scroll for the ~1024–1180px window
          (also hidden below 1024px via the caller). */}
      <div className="overflow-x-auto [scrollbar-width:thin]">
        <div className="min-w-[1080px]">
            {/* Header row */}
            <div
              className="grid gap-x-[14px]"
              style={{ gridTemplateColumns: "1.35fr 1.15fr 1fr 1fr 1fr" }}
            >
              <div />
              {/* Cello — top of the olive band */}
              <div
                className="text-center px-5 pt-[26px] pb-5"
                style={{
                  backgroundColor: "#403B1D",
                  borderRadius: "12px 12px 0 0",
                }}
              >
                <div
                  className="font-serif font-black text-[24px] sm:text-[28px]"
                  style={{ color: "#FCF6EC" }}
                >
                  Cello
                </div>
                <div
                  className="mt-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.09em]"
                  style={{ color: "rgba(250,241,232,0.6)" }}
                >
                  Full Closet Edit
                </div>
              </div>
              {[
                { name: "Traditional Stylist", sub: "Independent Stylists" },
                { name: "Stitch Fix", sub: "Subscription Boxes" },
                { name: "Fashivly", sub: "One-Time Style Guides" },
              ].map((c) => (
                <div key={c.name} className="text-center pt-[26px] pb-5 border-b-2 border-[#241F10]">
                  <div className="font-serif font-bold text-[16px] sm:text-[20px] text-[#241F10] leading-tight">
                    {c.name}
                  </div>
                  <div className="mt-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.09em] text-[#8A8268]">
                    {c.sub}
                  </div>
                </div>
              ))}
            </div>

            {/* Feature rows */}
            {ROWS.map((row, i) => {
              const isLast = i === ROWS.length - 1;
              return (
                <Fragment key={row.label}>
                  <div
                    className="grid gap-x-[14px]"
                    style={{ gridTemplateColumns: "1.35fr 1.15fr 1fr 1fr 1fr" }}
                  >
                    {/* Row label */}
                    <div
                      className={`flex items-center px-1 sm:px-2 py-4 text-[11px] sm:text-[12.5px] font-bold uppercase tracking-[0.1em] text-[#4E4936] leading-[1.4] ${
                        isLast ? "" : "border-b border-[#EFE8DB]"
                      }`}
                    >
                      {row.label}
                    </div>

                    {/* Cello cell — continuous olive band */}
                    <div
                      className="flex flex-col items-center justify-center gap-1.5 py-4 px-4"
                      style={{
                        backgroundColor: "#403B1D",
                        borderBottom: isLast ? "none" : "1px solid rgba(250,241,232,0.14)",
                        borderRadius: isLast ? "0 0 12px 12px" : undefined,
                      }}
                    >
                      {renderMark(row.cello.mark, true)}
                      {row.cello.caption && (
                        <div
                          className="text-[10px] sm:text-[11px] font-medium leading-snug text-center"
                          style={{ color: "rgba(250,241,232,0.6)" }}
                        >
                          {row.cello.caption}
                        </div>
                      )}
                    </div>

                    {/* Competitor cells */}
                    {[row.traditional, row.stitchFix, row.fashivly].map((cell, j) => (
                      <div
                        key={j}
                        className={`flex flex-col items-center justify-center gap-1.5 py-4 px-3 ${
                          isLast ? "" : "border-b border-[#EFE8DB]"
                        }`}
                      >
                        {renderMark(cell.mark, false)}
                        {cell.caption && (
                          <div className="text-[10px] sm:text-[11px] font-medium text-[#8A8268] leading-snug text-center">
                            {cell.caption}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>

      <p className="mt-6 max-w-[760px] font-serif italic text-[12.5px] sm:text-[13.5px] text-[#8A8268]">
        * Stitch Fix earns money by selling you their inventory.
      </p>
    </>
  );

  if (inline) {
    // Embedded (e.g. inside a FAQ answer). Caller controls the outer
    // container width and visibility — the table needs ~1080px to read.
    return <>{table}</>;
  }

  return (
    <section
      className="relative hidden lg:block w-full border-t border-[#e2d8ce] pt-[88px] pb-[112px] px-8 lg:px-[clamp(32px,6vw,108px)]"
      style={{
        backgroundColor: "#FAF1E8",
        backgroundImage:
          "radial-gradient(rgba(64,59,29,0.13) 1.3px, transparent 1.3px)",
        backgroundSize: "64px 64px",
      }}
    >
      <div className="max-w-[1160px] mx-auto">
        <div className="mb-14">
          <h2
            className="font-serif font-black leading-[1.05] tracking-[-0.5px] text-[#241F10]"
            style={{ fontSize: "clamp(40px, 4.2vw, 56px)" }}
          >
            How Cello compares
          </h2>
          <p className="mt-5 max-w-[600px] font-serif italic text-[18px] text-[#6B6449] leading-relaxed">
            One flat fee, your whole closet, a styling team all year — here's how that stacks up.
          </p>
        </div>
        {table}
      </div>
    </section>
  );
};

export default CompetitorCompareBass;
